import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { CampaignType, TriggerConfig } from './campaigns.api';

export interface CampaignTemplate {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  type: CampaignType;
  created_by: string;
  trigger_config: TriggerConfig | null;
  sequence_steps: Array<{
    step_number: number;
    type: 'EMAIL' | 'DELAY' | 'TASK' | 'BRANCH' | 'EXIT';
    delay_days?: number | null;
    subject?: string | null;
    body_html?: string | null;
    body_text?: string | null;
    template_id?: string | null;
    branch_condition?: unknown;
    task_title?: string | null;
    task_assignee_id?: string | null;
    task_due_days?: number | null;
  }>;
  max_steps: number;
  stop_on_reply: boolean;
  stop_on_bounce: boolean;
  stop_on_booking?: boolean;
  created_at: string;
  updated_at: string;
}

export function useCampaignTemplates() {
  return useQuery<{ data: CampaignTemplate[] }>({
    queryKey: ['campaign-templates'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: CampaignTemplate[] }>('/campaign-templates');
      return data;
    },
  });
}

export function useSaveCampaignAsTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { campaignId: string; name: string }) => {
      const { data } = await apiClient.post<{ data: CampaignTemplate }>('/campaign-templates', {
        campaign_id: args.campaignId,
        name: args.name,
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-templates'] }),
  });
}

export function useDeleteCampaignTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/campaign-templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-templates'] }),
  });
}
